import React from "react";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import HeroSection from "../components/home/HeroSection";
import { acquisitionApi } from "../api/acquisition";
import { useAuth } from "../context/AuthContext";
import {
  acquisitionCampaign,
  acquisitionDeviceMetadata,
  acquisitionEventId,
  acquisitionSessionId,
  acquisitionVisitorId,
  saveAcquisitionDraft,
} from "../utils/acquisitionFunnel";

// BLOCK 3 · Minimal acquisition-first landing + anonymous/authenticated traffic classification.
// STATUS: STATIC_PASS. Runtime/responsive verification pending staging deploy.
// Existing sales sections remain untouched in source; this page simply stops mounting them.
export default function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const [stats, setStats] = React.useState([]);
  const isAuthenticated = Boolean(user);
  const referralCode = String(searchParams.get("ref") || "").trim().toUpperCase();
  const sessionId = React.useMemo(() => acquisitionSessionId(), []);
  const visitorId = React.useMemo(() => acquisitionVisitorId(), []);

  React.useEffect(() => {
    acquisitionApi.publicStats().then((data) => setStats(Array.isArray(data?.stats) ? data.stats : [])).catch(() => setStats([]));
  }, []);

  React.useEffect(() => {
    // BLOCK 3 · Wait for auth resolution so owner/superadmin tests are classified
    // as authenticated and do not contaminate public pre-registration conversion.
    if (loading) return;
    const audience = isAuthenticated ? "authenticated" : "anonymous";
    const campaign = acquisitionCampaign(searchParams);
    acquisitionApi.startSession({ sessionId, visitorId, entryPoint: "home", referralCode, campaign, audience }).catch(() => null);
    acquisitionApi.track({
      action: "acquisition_landing_view",
      sessionId,
      visitorId,
      eventId: `landing_${sessionId}`.slice(0, 60),
      metadata: { stepKey: "landing", stepIndex: 0, totalSteps: 9, progressPct: 0, entryPoint: "home", audience, ...acquisitionDeviceMetadata() },
    }).catch(() => null);
  }, [isAuthenticated, loading, referralCode, searchParams, sessionId, visitorId]);

  const enterFunnel = (entryPoint = "start", need = "") => {
    if (loading) return;
    if (isAuthenticated && entryPoint === "start" && !need) {
      navigate("/dashboard");
      return;
    }

    saveAcquisitionDraft({ sessionId, visitorId, referralCode, ...(need ? { primaryNeed: need } : {}) });
    acquisitionApi.track({
      action: "acquisition_cta_clicked",
      sessionId,
      visitorId,
      eventId: acquisitionEventId("cta"),
      metadata: { stepKey: "landing", stepIndex: 0, totalSteps: 9, progressPct: 0, entryPoint, optionKey: need, audience: isAuthenticated ? "authenticated" : "anonymous", ...acquisitionDeviceMetadata() },
    }).catch(() => null);

    const params = new URLSearchParams();
    if (referralCode) params.set("ref", referralCode);
    if (entryPoint) params.set("entry", entryPoint);
    if (need) params.set("need", need);
    navigate(`/empecemos${params.toString() ? `?${params.toString()}` : ""}`);
  };

  return (
    <Box component="main" sx={{ width: "100%", overflowX: "hidden", bgcolor: "#0B1016", color: "#F7F8FA" }}>
      <HeroSection
        stats={stats}
        onStartClick={() => enterFunnel("start")}
        onExploreClick={() => enterFunnel("what-is")}
        onQuickNeed={(need) => enterFunnel("quick-need", need)}
        primaryLabel={loading ? "Cargando..." : isAuthenticated ? "Abrir mi panel" : "Empezar"}
        isAuthenticated={isAuthenticated}
        userName={user?.name || ""}
      />
    </Box>
  );
}
